import React, { useCallback, useEffect, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerAdminInvoices,
  getDealerAdminPayments,
  sendDealerAdminInvoice,
  voidDealerAdminInvoice,
  recordDealerAdminPayment,
  voidDealerAdminPayment,
  getDealerAdminInvoicePdf,
  syncDealerAdminInvoiceToZoho,
  linkDealerAdminInvoiceToZoho,
  openPdf,
  formatUsd,
  INVOICE_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
} from "../../../services/dealerGarage";
import InvoiceEditor from "./InvoiceEditor";
import { formatDate, invoiceRef, isOverdue, errorMessage, parseMoney, todayYmd, ORDER_CONFIRMED_NOTE } from "./dealerAdmin";

const STATUS_CHIP = {
  draft: [styles.chipMuted, "Draft"],
  sent: [styles.chipWarn, "Due"],
  paid: [styles.chipGood, "Paid"],
  void: [styles.chipMuted, "Void"],
};

export const InvoiceStatusChip = ({ invoice }) => {
  const overdue = isOverdue(invoice);
  const [cls, label] = overdue ? [styles.chipBad, "Overdue"] : (STATUS_CHIP[invoice.status] || [styles.chipMuted, invoice.status]);
  return <span className={`${styles.chip} ${cls}`}>{label}</span>;
};

// Its copy in Zoho Invoice (Backend/app/utils/zohoSync.js).
const ZOHO_CHIP = {
  synced: [styles.chipGood, "In Zoho"],
  linked: [styles.chipGood, "Linked in Zoho"],
  number_taken: [styles.chipWarn, "Number already used in Zoho"],
  error: [styles.chipBad, "Zoho sync failed"],
  void: [styles.chipMuted, "Void in Zoho"],
};

const ZohoChip = ({ invoice }) => {
  const zoho = invoice.zoho || {};
  const [cls, label] = ZOHO_CHIP[zoho.status] || [];
  if (!label) return null;
  // Renamed there once the chassis number came in.
  const renamed = zoho.number && zoho.number !== invoice.number && zoho.status !== "number_taken" ? ` as ${zoho.number}` : "";
  return <span className={`${styles.chip} ${cls}`}>{label}{renamed}</span>;
};

/** Record money received against one invoice. */
const PaymentForm = ({ invoice, onDone }) => {
  const balance = Math.max(0, (invoice.total || 0) - (invoice.amountPaid || 0));
  const [form, setForm] = useState({
    amount: balance ? String(Math.round(balance * 100) / 100) : "",
    method: invoice.source === "quickbooks" ? "quickbooks" : "wire",
    receivedAt: todayYmd(),
    reference: "",
    notes: "",
    notify: true,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseMoney(form.amount);
    if (!(amount > 0)) {
      alert("Enter the amount received.");
      return;
    }
    setBusy(true);
    try {
      const res = await recordDealerAdminPayment({ invoiceId: invoice._id, ...form, amount });
      if (res?.message === "Payment recorded") {
        if (res.orderConfirmed) alert(ORDER_CONFIRMED_NOTE);
        onDone(true);
      } else alert(errorMessage(res, "Could not record the payment."));
    } catch (err) {
      alert("Could not record the payment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.inlineForm} onSubmit={submit}>
      <div className={styles.grid2}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Amount received (USD)</label>
          <input className={classes.formInput} value={form.amount} inputMode="decimal" onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>How</label>
          <select className={classes.formInput} value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
            {Object.entries(PAYMENT_METHOD_LABELS)
              .filter(([k]) => !(k === "deposit" && invoice.kind === "deposit"))
              .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Received on</label>
          <input className={classes.formInput} type="date" value={form.receivedAt} onChange={(e) => setForm((f) => ({ ...f, receivedAt: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Reference</label>
          <input className={classes.formInput} value={form.reference} placeholder="Bank / QuickBooks reference" onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
        </div>
      </div>
      {form.method === "deposit" && <small className={styles.hint}>Taken from the customer's deposit balance.</small>}
      <label className={styles.checkboxRow}>
        <input type="checkbox" checked={form.notify} onChange={(e) => setForm((f) => ({ ...f, notify: e.target.checked }))} />
        Email the customer a receipt
      </label>
      <div className={classes.formButtons}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
          {busy ? "Saving..." : "Record payment"}
        </button>
        <button type="button" className={styles.btnText} onClick={() => onDone(false)}>Cancel</button>
      </div>
    </form>
  );
};

/**
 * Invoices and payments for one purchase, inspection or customer, with the
 * actions staff need on them: new invoice (ours or a QuickBooks record), send,
 * PDF, record payment, void - and, when the API mirrors invoices in Zoho
 * Invoice, each one's state there with Send / Retry / Link.
 *
 * Props:
 *   scope        { purchaseId } | { inspectionId } | { userId }
 *   defaultKind  kind for a new invoice ('vehicle', 'inspection', 'deposit', ...)
 *   onChange()   called after anything that changes money, so the parent can
 *                refresh its payment badge - and after a new car invoice
 *                changes the sale's quote, so the sale reloads too
 *   onEditingChange(open)  whether an invoice or payment form is open, so the
 *                parent can ask before closing over it
 */
const BillingPanel = ({ scope, defaultKind = "vehicle", onChange, onEditingChange }) => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { invoice } | { source, kind }
  const [payingId, setPayingId] = useState("");
  // Whether invoices are mirrored in Zoho here, and the one being sent there.
  const [zohoEnabled, setZohoEnabled] = useState(false);
  const [zohoBusyId, setZohoBusyId] = useState("");
  const scopeKey = JSON.stringify(scope);

  const editing = !!editor || !!payingId;
  useEffect(() => {
    if (!onEditingChange) return undefined;
    onEditingChange(editing);
    return () => onEditingChange(false);
  }, [editing, onEditingChange]);

  const load = useCallback(() => {
    const params = JSON.parse(scopeKey);
    setLoading(true);
    Promise.all([getDealerAdminInvoices(params), getDealerAdminPayments(params)])
      .then(([inv, pay]) => {
        setInvoices(inv?.message === "Success" ? inv.data : []);
        setZohoEnabled(!!inv?.zohoEnabled);
        setPayments(pay?.message === "Success" ? pay.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scopeKey]);

  useEffect(() => { load(); }, [load]);

  const changed = () => {
    load();
    if (onChange) onChange();
  };

  const resend = async (invoice) => {
    if (!window.confirm(`Email invoice ${invoice.number} to ${invoice.billTo && invoice.billTo.email} again?`)) return;
    const res = await sendDealerAdminInvoice(invoice._id, true).catch(() => null);
    alert(res?.emailed ? "Sent." : errorMessage(res, "The email did not go out."));
  };

  const voidInvoice = async (invoice) => {
    const reason = window.prompt(`Void ${invoiceRef(invoice)}? Give a reason (shown to staff only):`);
    if (reason === null) return;
    const res = await voidDealerAdminInvoice(invoice._id, reason).catch(() => null);
    if (res?.message === "Invoice voided" || res?.message === "Already void") changed();
    else alert(errorMessage(res, "Could not void the invoice."));
  };

  const voidPayment = async (payment) => {
    if (!window.confirm(`Void the ${formatUsd(payment.amount)} payment from ${formatDate(payment.receivedAt)}?`)) return;
    const res = await voidDealerAdminPayment(payment._id).catch(() => null);
    if (res?.message === "Payment voided" || res?.message === "Already void") changed();
    else alert(errorMessage(res, "Could not void the payment."));
  };

  // Send it to Zoho now, or retry after a failure.
  const syncToZoho = async (invoice) => {
    setZohoBusyId(invoice._id);
    const res = await syncDealerAdminInvoiceToZoho(invoice._id).catch(() => null);
    setZohoBusyId("");
    if (!res?.data) alert(errorMessage(res, "Could not reach the server."));
    load();
  };

  // Zoho already has an invoice under this number, and staff say it is this sale's.
  const linkInZoho = async (invoice) => {
    const question = `${invoice.zoho.error}\n\nLink ${invoiceRef(invoice)} to that Zoho invoice? It stays as it is in Zoho. `
      + "Payments recorded here from now on are added to it; earlier ones are taken to be in Zoho already.";
    if (!window.confirm(question)) return;
    setZohoBusyId(invoice._id);
    const res = await linkDealerAdminInvoiceToZoho(invoice._id).catch(() => null);
    setZohoBusyId("");
    if (!res?.data) alert(errorMessage(res, "Could not link it."));
    load();
  };

  const paymentsFor = (invoice) => payments.filter((p) => (p.invoiceId && (p.invoiceId._id || p.invoiceId)) === invoice._id);

  return (
    <div>
      {loading && invoices.length === 0 && <p className={styles.hint}>Loading invoices...</p>}
      {!loading && invoices.length === 0 && !editor && <p className={styles.hint}>No invoices yet.</p>}

      {invoices.map((invoice) => {
        const balance = Math.max(0, (invoice.total || 0) - (invoice.amountPaid || 0));
        const rows = paymentsFor(invoice);
        if (editor && editor.invoice && editor.invoice._id === invoice._id) return null;
        const zoho = (zohoEnabled && invoice.source === "site" && invoice.status !== "draft" && invoice.zoho) || {};
        const zohoBusy = zohoBusyId === invoice._id;
        // Not in Zoho yet (sent before the sync, or it failed): send it now.
        const canSendToZoho = zohoEnabled && invoice.source === "site"
          && (["sent", "paid"].includes(invoice.status) ? !zoho.status || zoho.status === "error" : invoice.status === "void" && zoho.status === "error");
        return (
          <div key={invoice._id} className={styles.invoiceRow}>
            <div className={styles.invoiceTop}>
              <strong>{invoiceRef(invoice)}</strong>
              <span className={`${styles.chip} ${styles.chipMuted}`}>{INVOICE_KIND_LABELS[invoice.kind] || invoice.kind}</span>
              <InvoiceStatusChip invoice={invoice} />
              {zoho.status && <ZohoChip invoice={invoice} />}
              {invoice.dueDate && invoice.status === "sent" && <span className={styles.hint}>due {formatDate(invoice.dueDate)}</span>}
              <span className={styles.invoiceAmounts}>
                {formatUsd(invoice.total)}
                {invoice.amountPaid > 0 && ` · paid ${formatUsd(invoice.amountPaid)}`}
                {invoice.status === "sent" && ` · ${formatUsd(balance)} due`}
              </span>
            </div>
            {zoho.error && <p className={styles.hint} style={{ margin: "6px 0 0" }}>Zoho: {zoho.error}</p>}
            {rows.length > 0 && (
              <ul className={styles.history} style={{ marginTop: 8 }}>
                {rows.map((p) => (
                  <li key={p._id} style={p.voided ? { opacity: 0.5, textDecoration: "line-through" } : undefined}>
                    <span>{formatDate(p.receivedAt)}</span>
                    <span>
                      {formatUsd(p.amount)} · {PAYMENT_METHOD_LABELS[p.method] || p.method}
                      {p.reference ? ` · ${p.reference}` : ""}
                      {p.recordedBy && p.recordedBy.firstName ? ` · by ${p.recordedBy.firstName}` : ""}
                      {!p.voided && (
                        <button type="button" className={styles.btnDangerText} style={{ padding: "0 6px" }} onClick={() => voidPayment(p)}>
                          void
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className={classes.formButtons} style={{ flexWrap: "wrap" }}>
              {invoice.status === "sent" && (
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setPayingId(payingId === invoice._id ? "" : invoice._id)}>
                  Record payment
                </button>
              )}
              {(invoice.status === "draft" || invoice.source === "quickbooks") && invoice.status !== "void" && (
                <button type="button" className={styles.btn} onClick={() => setEditor({ invoice })}>
                  {invoice.status === "draft" ? "Edit draft" : "Edit"}
                </button>
              )}
              {invoice.source === "site" && (
                <button type="button" className={styles.btn} onClick={() => openPdf(() => getDealerAdminInvoicePdf(invoice._id)).catch((e) => alert(e.message))}>
                  PDF
                </button>
              )}
              {invoice.source === "site" && invoice.status !== "draft" && invoice.status !== "void" && (
                <button type="button" className={styles.btn} onClick={() => resend(invoice)}>Resend</button>
              )}
              {canSendToZoho && (
                <button type="button" className={styles.btn} disabled={zohoBusy} onClick={() => syncToZoho(invoice)}>
                  {zohoBusy ? "Sending to Zoho..." : zoho.status === "error" ? "Retry Zoho" : "Send to Zoho"}
                </button>
              )}
              {zoho.status === "number_taken" && (
                <>
                  <button type="button" className={styles.btn} disabled={zohoBusy} onClick={() => linkInZoho(invoice)}>Link to that Zoho invoice</button>
                  <button type="button" className={styles.btnText} disabled={zohoBusy} onClick={() => syncToZoho(invoice)}>Check Zoho again</button>
                </>
              )}
              {invoice.status !== "draft" && invoice.status !== "void" && (
                <button type="button" className={styles.btnDangerText} onClick={() => voidInvoice(invoice)}>Void invoice</button>
              )}
            </div>
            {payingId === invoice._id && (
              <PaymentForm invoice={invoice} onDone={(didSave) => { setPayingId(""); if (didSave) changed(); }} />
            )}
          </div>
        );
      })}

      {editor && (
        <InvoiceEditor
          invoice={editor.invoice || null}
          source={editor.source}
          kind={editor.kind}
          scope={scope}
          onDone={() => { setEditor(null); changed(); }}
          onSaleChanged={onChange}
        />
      )}

      {!editor && (
        <div className={classes.formButtons}>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setEditor({ source: "site", kind: defaultKind })}>
            + New invoice (PDF, wire/ACH)
          </button>
          <button type="button" className={styles.btn} onClick={() => setEditor({ source: "quickbooks", kind: defaultKind })}>
            + Log a QuickBooks invoice
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingPanel;
